import { fromJS } from 'immutable';

import { DefaultDataGenerator } from '@pearsontechnology/aero-plugin-schema';

import { isImmutable } from 'utils/immutableHelpers';
import {
  STATE,
  SCHEMA,
} from 'services/coursewareItem/constants';

import { decomposeState } from './scope/decomposeState';

/**
 * Find the config,schema,state,scope information based on the sourceId passed
 * @param  _state :: Current state object
 * @param  sourceId :: sourceId
 * @returns targetConfig, targetScope
 */
export function getTheConfigAndScopeInformation(_state, sourceId) {
  const config = _state.getIn([sourceId, 'config']);
  const schema = _state.getIn([sourceId, 'schema']);
  const state = _state.getIn([sourceId, 'state']);
  const { scope } = decomposeState(schema, state);
  return {
    targetConfig: config,
    targetScope: scope,
  };
}

/**
 * Get the sourceConfigImmutable, pathsContainingTargetId based on the sourceConfig, targetId passed
 * @param  {Object} sourceConfig :: source config object
 * @param  {string} targetId :: the id of the element within the bound reference object
 * @param  {Object} _state :: instance of StateProvider
 * @returns sourceConfigImmutable, pathsContainingTargetId
 */
export function getConfigAndPaths(sourceConfig, targetId, _state) {
  const sourceConfigImmutable = fromJS(sourceConfig);
  const pathsContainingTargetId = getBoundReferencePathsByTargetId(sourceConfig, targetId, _state);

  return {
    sourceConfigImmutable,
    pathsContainingTargetId,
  };
}
/**
 * The function gets the resolved config based on the targetId passed
 * @param {Object} sourceConfig :: source config object
 * @param {Object} resolvedImmutable :: resolvedImmutable object
 * @param {string} targetId :: the id of the element within the bound reference object
 * @param  {Object} _state :: instance of StateProvider
 * @returns {Object}  resolvedImmutableConfig
 */
export function getResolvedImmutableConfig(_state, sourceConfig, targetId, resolvedImmutable) {
  const { sourceConfigImmutable, pathsContainingTargetId } = getConfigAndPaths(sourceConfig, targetId, _state);
  let resolvedImmutableConfig = fromJS(resolvedImmutable.toJS());
  // const targetConfigImmutable = fromJS(targetConfig);

  pathsContainingTargetId.forEach(({ path, sourceId }) => {
    const targetConfigImmutable = _state.getIn([sourceId, 'state']);
    const targetValue = targetConfigImmutable && fromJS(targetConfigImmutable).getIn(sourceConfigImmutable.getIn([...path, 'context']));

    if (targetValue !== undefined) {
      resolvedImmutableConfig = resolvedImmutableConfig.setIn(path, targetValue);
    } else {
      const generator = new DefaultDataGenerator();
      const propertySchema = sourceConfigImmutable.getIn(path).toJS();
      const defaultValue = generator.generateDefaultData(propertySchema);
      resolvedImmutableConfig = resolvedImmutableConfig.setIn(path, defaultValue?.schemaProperty);
    }
  });
  return resolvedImmutableConfig;
}


/**
 * The function gets the resolved scope config based on the targetId passed
 * @param {Object} _state :: instance of StateProvider
 * @param {Object} sourceConfig :: source config object
 * @param {Object} resolvedImmutable :: resolvedImmutable object
 * @param {string} targetId :: the id of the element within the bound reference object
 * @returns {Object}  resolvedImmutableScope
 */
export function getResolvedImmutableScope(_state, sourceConfig, targetId, resolvedImmutable) {
  const { sourceConfigImmutable, pathsContainingTargetId } = getConfigAndPaths(sourceConfig, targetId, _state);
  let resolvedImmutableScope = fromJS(resolvedImmutable.toJS());

  pathsContainingTargetId.forEach(({ path, sourceId: resolvedId }) => {
    const { targetScope } = getTheConfigAndScopeInformation(_state, resolvedId);
    const targetScopeImmutable = fromJS(targetScope);
    const targetValue = targetScopeImmutable.getIn(sourceConfigImmutable.getIn([...path, 'context']).toJS());
    if (targetValue !== undefined) {
      resolvedImmutableScope = resolvedImmutableScope.setIn(path, targetValue);
    }
  });
  return resolvedImmutableScope;
}

/**
 * The function gets the resolved scope config based on the targetId passed
 * @param {Object} _state :: instance of StateProvider
 * @param {Object} sourceConfig :: source config object
 * @param {string} targetId :: the id of the element within the bound reference object
 * @param {string} activityId :: activityId
 * @param {Object} resolvedImmutable :: resolvedImmutable object
 * @returns {Object}  resolvedImmutableScope
 */
export function getResolvedImmutableScopeOtherScreen(_state, sourceConfig, targetId, activityId, resolvedImmutable) {
  const { sourceConfigImmutable, pathsContainingTargetId } = getConfigAndPaths(sourceConfig, targetId, _state);
  let resolvedImmutableScope = fromJS(resolvedImmutable.toJS());

  pathsContainingTargetId.forEach(({ path, sourceId: resolvedId }) => {
    const activityScope = _state.getIn([activityId, 'scope']);
    const targetScope = activityScope && activityScope.find((scopeObj) => scopeObj.sourceId === resolvedId);
    let targetScopeData;
    if (targetScope) {
      targetScopeData = JSON.parse(targetScope.data);
    }

    if (targetScopeData) {
      const targetScopeImmutable = fromJS(targetScopeData);
      const targetValue = targetScopeImmutable.getIn(sourceConfigImmutable.getIn([...path, 'context']).toJS());
      if (targetValue) {
        resolvedImmutableScope = resolvedImmutableScope.setIn(path, targetValue);
      }
    }
  });
  return resolvedImmutableScope;
}

/**
 * The function finds the paths of all bound reference containing a particular sourceId
 * @param  {Object} _state :: instance of StateProvider
 * @param  {string} type :: resolver type - CONFIG || SCOPE
 * @param  {string} sourceId :: the id of the element containing the bound reference
 * @param  {string} targetId :: the id of the element within the bound reference object
 * @param  {string} activityId
 * @returns {Object} Resolved object for element with id equals sourceId
 */
export function resolveBoundReferenceByTypeAndSourceId(_state, type, sourceId, targetId, activityId) {
  const sourceConfig = _state.getIn([sourceId, 'config']);
  const targetConfig = _state.getIn([targetId, 'state']);
  const state = _state.getIn([sourceId, STATE]);
  const schema = _state.getIn([sourceId, SCHEMA]);
  const { scope: sourceScope } = decomposeState(schema, state);
  let resolvedImmutable = type === 'config' ? fromJS(sourceConfig) : fromJS(sourceScope);

  if (type === 'config') {
    resolvedImmutable = getResolvedImmutableConfig(_state, sourceConfig, targetId, resolvedImmutable);
  } else if (targetConfig && type === 'scope') {
    resolvedImmutable = getResolvedImmutableScope(_state, sourceConfig, targetId, resolvedImmutable);
  } else if (!targetConfig && type === 'scope') {
    resolvedImmutable = getResolvedImmutableScopeOtherScreen(_state, sourceConfig, targetId, activityId, resolvedImmutable);
  }

  return resolvedImmutable ? resolvedImmutable.toJS() : {};
}

/**
 * This fuction finds all bound reference paths of a particular property nested inside a config object
 * @param  {Object} nestedPropertyKey
 * @param  {Object} nestedPropertyValue
 * @param  {Object} inputConfig :: config object
 * @returns {Array<Array<string>>} Array of paths. Each path is an array of strings.
 */
export function getBoundReferencePathsOfNestedProperty(nestedPropertyKey, nestedPropertyValue, inputConfig) {
  const configImmutable = isImmutable(inputConfig) ? inputConfig : fromJS(inputConfig);
  const localPaths = getBoundReferencePaths(nestedPropertyValue);
  const nestedReferencePaths = localPaths.map((path) => {
    const flattenedPath = path.flat();
    const nestedPathExists = configImmutable.getIn([nestedPropertyKey, ...[flattenedPath]], 'schemaProperty');
    if (nestedPathExists) {
      return ([nestedPropertyKey, ...flattenedPath]);
    }
    return [];
  });

  return nestedReferencePaths;
}

/**
 * This fuction finds all bound references within a config and returns the path of said references
 * @param  {Object} inputConfig :: config object
 * @returns {Array<Array<string>>} Array of paths. Each path is an array of strings.
 */
export function getBoundReferencePaths(inputConfig) {
  if (!inputConfig) return [];
  const config = isImmutable(inputConfig) ? inputConfig.toJS() : { ...inputConfig };
  const paths = Object.entries(config).reduce((accumulator, [configKey, configValue]) => {
    if (configValue === null) {
      return accumulator;
    }
    if (typeof configValue === 'object' && configValue.schemaProperty) {
      return [...accumulator, [configKey]];
    }
    if (typeof configValue === 'object' && configValue.constructor === Object) {
      const referencePathsOfNestedProperty = getBoundReferencePathsOfNestedProperty(configKey, configValue, inputConfig);
      return [...accumulator, ...referencePathsOfNestedProperty];
    }
    if (Array.isArray(configValue)) {
      configValue.forEach((item, index) => {
        const nestedPaths = getBoundReferencePaths(item).flat();
        if (nestedPaths && nestedPaths.length > 0) {
          // if two siblings of the Array Object are value binded
          if (nestedPaths.length === 1) {
            accumulator.push([configKey, index, ...nestedPaths]);
          } else {
            nestedPaths.forEach((path) => {
              accumulator.push([configKey, index, path]);
            });
          }
        }
      });
    }
    return accumulator;
  }, []);
  return paths;
}

/**
 * The function finds the paths of all bound reference containing a particular sourceId
 * @param  {Object} inputConfig :: config object
 * @param  {Object} sourceId :: sourceId of the bound reference
 * @param  {Object} _state :: instance of StateProvider
 * @returns {Array<Array<object>>} Array of Objects conatining paths and targetId.
 */
export function getBoundReferencePathsByTargetId(inputConfig, sourceId, _state) {
  const boundReferencePaths = getBoundReferencePaths(inputConfig);
  const configImmutable = isImmutable(inputConfig) ? inputConfig : fromJS(inputConfig);
  const referencePathsOfsourceId = [];

  boundReferencePaths.forEach((path) => {
    if (configImmutable.getIn([...path, 'sourceId']) === sourceId) {
      referencePathsOfsourceId.push({ path, sourceId });
    } else {
      const inputConfigSourceId = configImmutable.getIn([...path, 'sourceId']);
      const targetState = _state.getIn([inputConfigSourceId, STATE]);
      if (targetState) {
        /*
          This is a hotfix for a bug. - need to address properly later
          For some reason when we resolve the binding and set state for one binding pair,
          this state update is not reflected when other binding resolver is triggered.
          The below push all bound references within a config to the paths array. This resolves the above issue.
          Need to investigate why the issue is happening and clean up this code.
        */
        referencePathsOfsourceId.push({ path, sourceId: inputConfigSourceId });
      }
    }
  });

  return referencePathsOfsourceId;
}

/**
   * Gets all bounded reference list based on courseWareList
   * @param coursewareList
   * @returns boundedReferencesList
   */
export function getBoundReferences(coursewareList) {
  let boundedReferencesList = [];
  Object.keys(coursewareList).forEach((key) => {
    const configData = coursewareList[key] && coursewareList[key].config;
    if (configData && Object.keys(configData).length > 0) {
      boundedReferencesList = createBoundReferenceList(configData, coursewareList[key].id, boundedReferencesList);
    }
  });
  return boundedReferencesList;
}

/**
 * Based on each config object from configData , find if it is having property with "sourceId"
 * that means its value binded , if found push all the references to boundedReferencesList.
 * else invoke createBoundReferenceList again
 * @param  configValue
 * @param  id
 * @param boundedReferencesList
 * @returns boundedReferencesList
 */

function createBoundedList(configValue, id, boundedReferencesList) {
  if (configValue && configValue?.hasOwnProperty('sourceId')) {
    boundedReferencesList.push({ ...configValue, id });
  } else {
    createBoundReferenceList(configValue, id, boundedReferencesList);
  }
}

/**
 * Based on each config object from configData , determine if its Array Object
 * or simple object. Accordingly then invoke createBoundedList to create the
 * boundedReferencesList object
 * @param  configData
 * @param  id
 * @param boundedReferencesList
 * @returns boundedReferencesList
 */
function createBoundReferenceList(configData, id, boundedReferencesList) {
  Object.keys(configData).forEach((config) => {
    const isConfigDataArray = configData[config] instanceof Array;
    // create a list only for those type which is not Array and has object as its values

    if (configData[config] instanceof Object) {
      if (!isConfigDataArray) {
        createBoundedList(configData[config], id, boundedReferencesList);
      } else {
        configData[config].forEach((configItem) => {
          if (configItem instanceof Object) {
            Object.entries(configItem).forEach(([, value]) => {
              if (value instanceof Object) {
                createBoundedList(value, id, boundedReferencesList);
              }
            });
          }
        });
      }
    }
  });
  return boundedReferencesList;
}
/**
 * Returns the matching object for the element Type
 * @param  _state
 * @param elementType
 * @returns matching object for the elementType
 */
export function getObjectBasedOnElementType(_state, elementType) {
  return _state.state.find((obj) => obj.get('elementType') === elementType)?.toJS();
}
